import React from 'react';
import '../../App.css';
import '../data-management/data-management.css';
import '../../tools/search-results/search-results.css';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// types
import {
  DataItemType,
  RucioLocationsType,
  Site,
  SiteStorageArea,
} from '../../utils/types';
import { Event, EventType, EventTarget, MoveDataToStorage } from '../../utils/events';

// functions
import { ContainerHook, siteName } from '../../utils/functions';

// classes
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import ProjectFilesTable from './project-files-table';
import { ContextMenu } from '../menus/context-menu';
import Scrollbox from '../../tools/scrollbox';
import ComputeServicesPanel from '../panels/compute-services-panel';
import MiniButton from '../../tools/mini-button';
import SiteIcon from '../../tools/site-icon';
import DataStagingComponent from '../data-management/data-staging-component';

// icons
import dataStagingIcon from '../../icons/data-staging.512.png';
import dataStagingIconDisabled from '../../icons/data-staging-bw.512.png';
import selectAllIcon from '../../icons/select-all.256.png';
import selectAllIconDisabled from '../../icons/select-all-bw.256.png';
import deselectAllIcon from '../../icons/deselect-all.256.png';
import deselectAllIconDisabled from '../../icons/deselect-all-bw.256.png';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions (redux).
import {
  setComputeMaximised,
  setContextMenuRefresh,
  setLifetimeStr,
  setLifetime,
  setSelectedDivs,
  setSelectedItems,
} from './project-slice';
import { fnD_countAvailableFiles } from '../../app/data-actions';
import {
  CONTEXT_SYSTEMS,
  CONTEXT_CONTAINERS,
  CONTEXT_MENU_DATA_STAGING as CONTEXT_MENU,
  ctx_setContextRow,
  ctx_setContextVisible,
} from '../menus/context-menu-slice';
import {
  ctx_buildContextMenu,
  ctx_calculatePosition,
  ctx_menuDisplayed,
} from '../menus/context-menu-actions';
import { handleEvent } from '../../app-actions';
import { getRucioSites } from '../data-management/file-browser-actions';

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function ProjectFiles() {
  //
  //	--------------------------------------------------------------------------
  //
  //	C O N T E X T   M E N U   D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const CONTEXT_SYSTEM: CONTEXT_SYSTEMS = CONTEXT_SYSTEMS.PROJECT_DATA_FILES;

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state parameters.
  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  // data
  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);
  const sD_rucioLocations: RucioLocationsType[] = useAppSelector(
    (state) => state.data.rucioLocations,
  );
  const sD_storageAreas: SiteStorageArea[] = useAppSelector(
    (state) => state.data.storageAreas,
  );

  // lifetime.
  const sLifetimeStr: string = useAppSelector((state) => state.project.lifetimeStr);
  const sLifetime: number = useAppSelector((state) => state.project.lifetime);

  // selected items.
  const sSelectedItems: DataItemType[] = useAppSelector(
    (state) => state.project.selectedItems,
  );
  const sSelectedDivs: number[] = useAppSelector((state) => state.project.selectedDivs);

  const sComputeMaximised: boolean = useAppSelector(
    (state) => state.project.computeMaximised,
  );

  // the data items displayed in the table.
  const sProjectItems: DataItemType[] = useAppSelector(
    (state) => state.project.projectItems,
  );

  // context menu refresh.
  const sContextMenuRefresh: number = useAppSelector(
    (state) => state.project.contextMenuRefresh,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // monitor this container, and if it changes size update the state.
  const dataWindow = ContainerHook({ container: CONTEXT_CONTAINERS.PROJECT });

  // retrieve the list of sites (in availability order) for the project
  // items and selected project items.
  const sites: {
    site: Site;
    count: number;
  }[] = getRucioSites({
    allItems: sProjectItems,
    selectedItems: sSelectedItems,
    catalog: 'project-files',
  });

  //	--------------------------------------------------------------------------
  //
  //	E V E N T   D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Handles clicks on toolbar buttons and contextual UI elements
  //	tied to the File Browser
  //
  //	------------------------------------------------------------

  function handleToolbarActions(event: React.MouseEvent<HTMLButtonElement>) {
    //
    const id: string = event.currentTarget.id;

    switch (id) {
      //
      case 'stage-selected':
        //
        if (sSelectedItems.length > 0) {
          //
          // update the text in the main context menu.
          ctx_buildContextMenu({
            contextSystem: CONTEXT_SYSTEM,
            contextMenu: CONTEXT_MENU.SITES,
            parentRowID: -1,
          });

          // check if this position is OK - use the button as a references point.
          ctx_calculatePosition({
            contextSystem: CONTEXT_SYSTEM,
            contextMenu: CONTEXT_MENU.SITES,
            button: id,
          });

          dispatch(
            ctx_setContextVisible({
              contextSystem: CONTEXT_SYSTEM,
              contextMenu: CONTEXT_MENU.MAIN,
              visible: false,
            }),
          );
          dispatch(
            ctx_setContextVisible({
              contextSystem: CONTEXT_SYSTEM,
              contextMenu: CONTEXT_MENU.SITES,
              visible: true,
            }),
          );

          dispatch(
            ctx_setContextRow({
              contextSystem: CONTEXT_SYSTEM,
              row: -1,
            }),
          );

          dispatch(setContextMenuRefresh(Date.now()));
          //
        }

        break;

      case 'select-all':
        //
        let selectedDivs: number[] = [];
        for (let i: number = 0; i < sProjectItems.length; i++) {
          selectedDivs.push(i);
        }
        dispatch(setSelectedDivs(selectedDivs));

        break;

      case 'deselect':
        //
        dispatch(setSelectedDivs([]));

        break;
      //
    }
    //
  } // handleToolbarActions

  //	------------------------------------------------------------
  //
  //	Handler button clicks.
  //
  //	------------------------------------------------------------

  const onClickBtnHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // Handle toolbar actions (e.g. icon size, staging, etc.)
    handleToolbarActions(event);

    // prevent propogation of the onClick event.
    event.stopPropagation();

    // stage the data to the recommended site.
    if (button.name === 'stage-data') {
      //
      // build a list of selected files.
      let filesToMove: DataItemType[] = [];

      // add the selected items.
      if (sSelectedItems.length > 0) {
        filesToMove = sSelectedItems.map((map: DataItemType) => {
          return {
            namespace: map.namespace,
            filename: map.filename,
            did_type: 'FILE',
            bytes: 0,
          };
        });
      } else {
        filesToMove = sProjectItems.map((map: DataItemType) => {
          return {
            namespace: map.namespace,
            filename: map.filename,
            did_type: 'FILE',
            bytes: 0,
          };
        });
      }

      if (sites.length > 0) {
        //
        const siteIndex: number = sD_sites.findIndex(
          (findSite) => findSite.site_uuid === sites[0].site.site_uuid,
        );
        const storageAreaIndex: number = 0;

        // moving data to a storage location.
        if (siteIndex > -1) {
          if (sD_storageAreas[siteIndex].storage_areas.length > 0) {
            //
            const moveDataToStorage: MoveDataToStorage = {
              toStorageAreaUUID:
                sD_storageAreas[siteIndex].storage_areas[storageAreaIndex].storage_id,
              toSiteUUID: sD_storageAreas[siteIndex].site.site_uuid ?? '',
              toStorageIdentifier:
                sD_storageAreas[siteIndex].storage_areas[storageAreaIndex].name,
              lifetime: 3600,
              filesToMove: filesToMove,
            };
            const newEvent: Event = {
              eventTarget: EventTarget.APP,
              eventType: EventType.MOVE_DATA_TO_STORAGE,
              parameters: moveDataToStorage,
            };
            dispatch(handleEvent({ event: newEvent }));

            //dispatch(setSubmittingJob(true));
            //
          }
        }
        //
      }
      //
    }

    // stage the data to a specific site.
    if (button.name === 'stage-to') {
      //
      // if the menu is currently displayed then hide it.
      if (ctx_menuDisplayed({ contextSystem: CONTEXT_SYSTEM }) === true) {
        dispatch(
          ctx_setContextVisible({
            contextSystem: CONTEXT_SYSTEM,
            visible: false,
          }),
        );
      } else {
        //
        // update the text in the main context menu.
        ctx_buildContextMenu({
          contextSystem: CONTEXT_SYSTEM,
          contextMenu: CONTEXT_MENU.SITES,
          parentRowID: -1,
        });

        // check if this position is OK - use the button as a references point.
        ctx_calculatePosition({
          contextSystem: CONTEXT_SYSTEM,
          contextMenu: CONTEXT_MENU.SITES,
          button: button.name,
        });

        dispatch(
          ctx_setContextVisible({
            contextSystem: CONTEXT_SYSTEM,
            contextMenu: CONTEXT_MENU.MAIN,
            visible: false,
          }),
        );
        dispatch(
          ctx_setContextVisible({
            contextSystem: CONTEXT_SYSTEM,
            contextMenu: CONTEXT_MENU.SITES,
            visible: true,
          }),
        );

        dispatch(
          ctx_setContextRow({
            contextSystem: CONTEXT_SYSTEM,
            row: -1,
          }),
        );

        // turn off the stage-selected flag because we will be staging everything.
        dispatch(setContextMenuRefresh(Date.now()));

        // prevent propogation of the onClick event.
        event.stopPropagation();
        //
      }
      //
    }
    //
  }; // onClickBtnHandler

  //	------------------------------------------------------------
  //
  //	Handler for div onClick events.
  //
  //	------------------------------------------------------------

  const onClickDivHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    //
    const div: HTMLDivElement = event.currentTarget;

    if (div.id === 'project-container') {
      dispatch(
        ctx_setContextVisible({
          contextSystem: CONTEXT_SYSTEM,
          visible: false,
        }),
      );
    }
    //
  }; // onClickDivHandler

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T S
  //
  //	--------------------------------------------------------------------------

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    //
    <div
      style={{
        flex: '1 1',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        className="gradient-background text-colour"
        style={{
          flex: '1 1',
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <div
          id="project-container"
          ref={dataWindow}
          role="region"
          aria-label="Project"
          onClick={onClickDivHandler}
          style={{
            margin: '0px 0px 0px 0px',
            padding: '10px 10px 10px 10px',
            height: '100%',
            flex: '1 1',
            display: 'flex',
            flexDirection: 'column',
            fontWeight: '400',
            position: 'relative',
          }}
        >
          <div
            style={{
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: 'row',
              margin: '3px',
            }}
          >
            <div
              id="lbl-project-files"
              role="heading"
              aria-level={2}
              className="header-text"
              style={{ flex: '0 0 auto' }}
            >
              {'Project data' +
                (sG_loggedIn === true && sG_project !== undefined
                  ? ' (' + (sG_project?.name ?? 'unknown').toUpperCase() + ')'
                  : '')}
            </div>
            <div style={{ flex: '1 1' }} />
          </div>
          <div
            className="text-colour"
            style={{
              flex: '0 0 auto',
              margin: '10px 0px 0px 3px',
            }}
          >
            Please select project files for staging. If no files are selected, then ALL
            files will be staged.
          </div>

          <ProjectFilesTable />

          {/* add the context menus */}
          <ContextMenu key={sContextMenuRefresh.toString()} system={CONTEXT_SYSTEM} />
        </div>

        {/*
         //  Column to contain the file details.
         */}
        <div
          className="divider"
          data-divider="LR"
          style={{
            flex: '0 0 auto',
            display: 'flex',
            height: '100%',
            flexDirection: 'column',
          }}
        >
          <DataStagingComponent
            allItems={sProjectItems}
            selectedItems={sSelectedItems}
            catalog="project-files"
            contextSystem={CONTEXT_SYSTEM}
            contextMenuRefresh={setContextMenuRefresh}
          />
        </div>

        {/*
         //  Column to contain the command buttons.
         */}
        <div
          style={{
            flex: '0 0 55px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div style={{ flex: '1 1' }} />
          <MiniButton
            id="select-all"
            data-selected="F"
            onClick={onClickBtnHandler}
            disabled={sSelectedDivs.length === sProjectItems.length}
          >
            <img
              src={
                sSelectedDivs.length < sProjectItems.length
                  ? selectAllIcon
                  : selectAllIconDisabled
              }
              alt="Select all items"
              width="34"
              height="34"
              title={t('Select all items')}
            />
          </MiniButton>
          <div style={{ flex: '0 0 5px' }} />
          <MiniButton
            id="deselect"
            data-selected="F"
            onClick={onClickBtnHandler}
            disabled={sSelectedDivs.length === 0}
          >
            <img
              src={sSelectedDivs.length > 0 ? deselectAllIcon : deselectAllIconDisabled}
              alt="Deselect all items"
              width="34"
              height="34"
              title={t('Deselect all items')}
            />
          </MiniButton>
          <div style={{ flex: '0 0 5px' }} />
          <div
            style={{
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
            }}
          >
            <div style={{ flex: '1 1' }} />
            <MiniButton
              id="stage-selected"
              data-selected="F"
              onClick={onClickBtnHandler}
              disabled={sSelectedDivs.length === 0}
            >
              <img
                src={sSelectedDivs.length > 0 ? dataStagingIcon : dataStagingIconDisabled}
                alt="Stage selected"
                width="34"
                height="34"
                title={t('Stage selected')}
              />
            </MiniButton>
            <div
              style={{
                flex: '1 1',
                marginLeft: '2px',
                alignItems: 'center',
                display: 'flex',
                color: sSelectedDivs.length > 0 ? '#5c85c4' : '#838383',
              }}
            >
              ▸
            </div>
          </div>
          <div style={{ flex: '0 0 5px' }} />
        </div>
      </div>

      <div style={{ flex: '0 0 5px' }} />
      <ComputeServicesPanel
        sites={sites.length > 0 ? [sites[0].site] : []}
        maximised={sComputeMaximised && sites.length > 0}
        minimised={!sComputeMaximised && sites.length > 0}
        setComputeMaximised={setComputeMaximised}
        flex="0 0 auto"
      />
    </div>
  );
} // ProjectFiles

//	--------------------------------------------------------------------------
//
//	S U B   C O M P O N E N T S
//
//	--------------------------------------------------------------------------
